import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { FiltersSheet } from "@/components/fleet/reports/FiltersSheet";
import { ExecutiveSummary } from "@/components/fleet/reports/ExecutiveSummary";
import { KPIPanel } from "@/components/fleet/reports/KPIPanel";
import { ReportHeader, ExportMenu } from "@/components/fleet/reports";
import { useConsolidatedReport, ConsolidatedFilters } from "@/hooks/use-consolidated-report";

import { 
  Car, Users, Wrench, Fuel, DollarSign, FileText, AlertTriangle, MapPin,
  CheckCircle, Clock, XCircle, AlertCircle, SlidersHorizontal, Download, Printer
} from "lucide-react";
import { 
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

const COLORS = ['hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))', 'hsl(var(--chart-5))'];

const DEFAULT_FILTERS: ConsolidatedFilters = {
  vehicles: true,
  drivers: true,
  maintenance: true,
  fuel: true,
  costs: true,
  documents: true,
  alerts: true,
  trips: true
};

export default function ConsolidatedReport() {
  const [filters, setFilters] = useState<ConsolidatedFilters>(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<ConsolidatedFilters>(DEFAULT_FILTERS);
  const [isExporting, setIsExporting] = useState(false);
  
  const { data, isLoading } = useConsolidatedReport(appliedFilters);

  const handleApply = () => {
    setAppliedFilters(filters);
  };

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
  };

  const formatCurrency = (value: number) => 
    `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

  const formatNumber = (value: number) => 
    value.toLocaleString('pt-BR');

  const handleExportPDF = async () => {
    if (!data) return;
    setIsExporting(true);
    // TODO: Implementar exportação PDF para relatório consolidado
    console.log('Exportar PDF - funcionalidade em desenvolvimento');
    setIsExporting(false);
  };

  const handleExportExcel = async () => {
    if (!data) return;
    setIsExporting(true);
    // TODO: Implementar exportação Excel para relatório consolidado
    console.log('Exportar Excel - funcionalidade em desenvolvimento');
    setIsExporting(false);
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-12 w-96" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const periodText = appliedFilters.dateRange?.from 
    ? `${format(appliedFilters.dateRange.from, "dd/MM/yyyy", { locale: ptBR })} - ${appliedFilters.dateRange.to ? format(appliedFilters.dateRange.to, "dd/MM/yyyy", { locale: ptBR }) : 'Atual'}`
    : 'Todo o período';

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Relatório Consolidado da Frota</h1>
          <p className="text-muted-foreground mt-1">
            Visão unificada de todos os dados operacionais • {periodText}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <FiltersSheet
            filters={filters}
            onFilterChange={setFilters}
            onApply={handleApply}
            onReset={handleReset}
            trigger={
              <Button variant="outline" className="gap-2">
                <SlidersHorizontal className="h-4 w-4" />
                Filtros
                <Badge variant="secondary" className="ml-1">
                  {Object.values(appliedFilters).filter(v => v === true).length}
                </Badge>
              </Button>
            }
          />
          <Button variant="outline" onClick={handleExportPDF} disabled={isExporting}>
            <Download className="h-4 w-4 mr-2" />
            PDF
          </Button>
          <Button variant="outline" onClick={handleExportExcel} disabled={isExporting}>
            <Download className="h-4 w-4 mr-2" />
            Excel
          </Button>
          <Button variant="outline" onClick={() => window.print()}>
            <Printer className="h-4 w-4 mr-2" />
            Imprimir
          </Button>
        </div>
      </div>

      {/* Executive Summary */}
      {data && (
        <ExecutiveSummary 
          data={{
            ...data,
            documents: { ...data.documents, valid: data.documents.total - data.documents.expiringSoon - data.documents.expired },
            alerts: { total: data.alerts.total, critical: data.alerts.critical, warning: data.alerts.high, info: data.alerts.medium },
            trips: { ...data.trips, avgDuration: 0 }
          }} 
          filters={appliedFilters} 
        />
      )}

      {/* KPI Panel */}
      {data && (
        <KPIPanel data={data} />
      )}

      {/* Detailed Analysis Tabs */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-5 h-12">
          <TabsTrigger value="overview" className="text-sm">Visão Geral</TabsTrigger>
          <TabsTrigger value="costs" className="text-sm">Análise de Custos</TabsTrigger>
          <TabsTrigger value="maintenance" className="text-sm">Manutenção</TabsTrigger>
          <TabsTrigger value="documents" className="text-sm">Documentos</TabsTrigger>
          <TabsTrigger value="alerts" className="text-sm">Alertas</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Vehicle Status */}
            {appliedFilters.vehicles && data?.vehicles && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Car className="h-5 w-5 text-blue-500" />
                    Status dos Veículos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Ativos', value: data.vehicles.active, fill: COLORS[1] },
                          { name: 'Inativos', value: data.vehicles.inactive, fill: COLORS[2] },
                          { name: 'Em Manutenção', value: data.vehicles.inMaintenance, fill: COLORS[3] }
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                        <Cell fill={COLORS[1]} />
                        <Cell fill={COLORS[2]} />
                        <Cell fill={COLORS[3]} />
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Cost Distribution */}
            {appliedFilters.costs && data?.costs && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-emerald-500" />
                    Distribuição de Custos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={[
                      { name: 'Combustível', value: data.costs.totalFuel, fill: COLORS[0] },
                      { name: 'Manutenção', value: data.costs.totalMaintenance, fill: COLORS[1] },
                      { name: 'Despesas', value: data.costs.totalExpenses, fill: COLORS[2] }
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `R$ ${(v/1000).toFixed(0)}k`} />
                      <Tooltip formatter={(value: any) => formatCurrency(Number(value))} />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                        <Cell fill={COLORS[0]} />
                        <Cell fill={COLORS[1]} />
                        <Cell fill={COLORS[2]} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Cost by Category Pie */}
            {appliedFilters.costs && data?.costs?.byCategory && data.costs.byCategory.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Custos por Categoria</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={data.costs.byCategory.slice(0, 5)}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        dataKey="value"
                        nameKey="category"
                        label={({ category, percent }) => `${category}: ${(percent * 100).toFixed(0)}%`}
                      >
                        {data.costs.byCategory.slice(0, 5).map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: any) => formatCurrency(Number(value))} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Top Vehicles by Cost */}
            {appliedFilters.costs && data?.costs?.byVehicle && data.costs.byVehicle.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Top 10 Veículos por Custo</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {data.costs.byVehicle.slice(0, 10).map((item, idx) => (
                      <div key={item.vehicle} className="flex items-center gap-3">
                        <span className="text-sm font-medium w-6 text-muted-foreground">{idx + 1}.</span>
                        <span className="text-sm font-medium w-20 truncate">{item.vehicle}</span>
                        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary rounded-full transition-all"
                            style={{ width: `${(item.value / (data.costs.byVehicle[0]?.value || 1)) * 100}%` }}
                          />
                        </div>
                        <span className="text-sm text-muted-foreground w-28 text-right">
                          {formatCurrency(item.value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Costs Tab */}
        <TabsContent value="costs" className="space-y-6 mt-6">
          {appliedFilters.costs && data?.costs && (
            <>
              <div className="grid grid-cols-3 gap-4">
                <Card className="border-l-4 border-l-purple-500">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <Fuel className="h-8 w-8 text-purple-500" />
                      <div>
                        <div className="text-2xl font-bold">{formatCurrency(data.costs.totalFuel)}</div>
                        <div className="text-sm text-muted-foreground">Combustível</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-orange-500">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <Wrench className="h-8 w-8 text-orange-500" />
                      <div>
                        <div className="text-2xl font-bold">{formatCurrency(data.costs.totalMaintenance)}</div>
                        <div className="text-sm text-muted-foreground">Manutenção</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-emerald-500">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <DollarSign className="h-8 w-8 text-emerald-500" />
                      <div>
                        <div className="text-2xl font-bold">{formatCurrency(data.costs.totalExpenses)}</div>
                        <div className="text-sm text-muted-foreground">Outras Despesas</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Detalhamento por Veículo</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Veículo</TableHead>
                        <TableHead className="text-right">Custo Total</TableHead>
                        <TableHead className="text-right">% do Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.costs.byVehicle.map((item) => (
                        <TableRow key={item.vehicle}>
                          <TableCell className="font-medium">{item.vehicle}</TableCell>
                          <TableCell className="text-right">{formatCurrency(item.value)}</TableCell>
                          <TableCell className="text-right">
                            {((item.value / data.costs.grandTotal) * 100).toFixed(1)}%
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Maintenance Tab */}
        <TabsContent value="maintenance" className="space-y-6 mt-6">
          {appliedFilters.maintenance && data?.maintenance && (
            <>
              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6 flex items-center gap-3">
                    <Clock className="h-8 w-8 text-yellow-500" />
                    <div>
                      <div className="text-2xl font-bold">{data.maintenance.pending}</div>
                      <div className="text-sm text-muted-foreground">Pendentes</div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 flex items-center gap-3">
                    <CheckCircle className="h-8 w-8 text-green-500" />
                    <div>
                      <div className="text-2xl font-bold">{data.maintenance.completed}</div>
                      <div className="text-sm text-muted-foreground">Concluídas</div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 flex items-center gap-3">
                    <Wrench className="h-8 w-8 text-blue-500" />
                    <div>
                      <div className="text-2xl font-bold">{data.maintenance.totalOrders - data.maintenance.pending - data.maintenance.completed}</div>
                      <div className="text-sm text-muted-foreground">Em Andamento</div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 flex items-center gap-3">
                    <DollarSign className="h-8 w-8 text-emerald-500" />
                    <div>
                      <div className="text-2xl font-bold">{formatCurrency(data.maintenance.totalCost)}</div>
                      <div className="text-sm text-muted-foreground">Custo Total</div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {data.maintenance.items && data.maintenance.items.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Últimas Ordens de Serviço</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>OS</TableHead>
                          <TableHead>Veículo</TableHead>
                          <TableHead>Descrição</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Custo</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.maintenance.items.slice(0, 10).map((wo: any) => (
                          <TableRow key={wo.id}>
                            <TableCell className="font-mono text-sm">{wo.order_number || wo.id.slice(0, 8)}</TableCell>
                            <TableCell>{wo.fleet_vehicles?.plate || '-'}</TableCell>
                            <TableCell className="max-w-xs truncate">{wo.description || '-'}</TableCell>
                            <TableCell>
                              <Badge variant={wo.status === 'completed' ? 'default' : 'secondary'}>
                                {wo.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">{formatCurrency(wo.total_cost || 0)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-6 mt-6">
          {appliedFilters.documents && data?.documents && (
            <>
              <div className="grid grid-cols-4 gap-4">
                <Card className="border-l-4 border-l-blue-500">
                  <CardContent className="pt-6 flex items-center gap-3">
                    <FileText className="h-8 w-8 text-blue-500" />
                    <div>
                      <div className="text-2xl font-bold">{data.documents.total}</div>
                      <div className="text-sm text-muted-foreground">Total</div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-green-500">
                  <CardContent className="pt-6 flex items-center gap-3">
                    <CheckCircle className="h-8 w-8 text-green-500" />
                    <div>
                      <div className="text-2xl font-bold">{data.documents.total - data.documents.expiringSoon - data.documents.expired}</div>
                      <div className="text-sm text-muted-foreground">Válidos</div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-yellow-500">
                  <CardContent className="pt-6 flex items-center gap-3">
                    <AlertCircle className="h-8 w-8 text-yellow-500" />
                    <div>
                      <div className="text-2xl font-bold">{data.documents.expiringSoon}</div>
                      <div className="text-sm text-muted-foreground">A Vencer</div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-red-500">
                  <CardContent className="pt-6 flex items-center gap-3">
                    <XCircle className="h-8 w-8 text-red-500" />
                    <div>
                      <div className="text-2xl font-bold">{data.documents.expired}</div>
                      <div className="text-sm text-muted-foreground">Vencidos</div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-6 mt-6">
          {appliedFilters.alerts && data?.alerts && (
            <>
              <div className="grid grid-cols-4 gap-4">
                <Card className="border-l-4 border-l-red-500">
                  <CardContent className="pt-6 flex items-center gap-3">
                    <AlertTriangle className="h-8 w-8 text-red-500" />
                    <div>
                      <div className="text-2xl font-bold">{data.alerts.critical}</div>
                      <div className="text-sm text-muted-foreground">Críticos</div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-yellow-500">
                  <CardContent className="pt-6 flex items-center gap-3">
                    <AlertCircle className="h-8 w-8 text-yellow-500" />
                    <div>
                      <div className="text-2xl font-bold">{data.alerts.high}</div>
                      <div className="text-sm text-muted-foreground">Alta Prioridade</div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-blue-500">
                  <CardContent className="pt-6 flex items-center gap-3">
                    <AlertCircle className="h-8 w-8 text-blue-500" />
                    <div>
                      <div className="text-2xl font-bold">{data.alerts.medium}</div>
                      <div className="text-sm text-muted-foreground">Média Prioridade</div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-gray-500">
                  <CardContent className="pt-6 flex items-center gap-3">
                    <CheckCircle className="h-8 w-8 text-gray-500" />
                    <div>
                      <div className="text-2xl font-bold">{data.alerts.total - data.alerts.critical - data.alerts.high - data.alerts.medium}</div>
                      <div className="text-sm text-muted-foreground">Outros</div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
